import classNames from 'classnames';

import Toggle from 'components/partials/toggle/toggle';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';
import { CoaCurrentYearAmountInput } from './components/coa-current-year-amount-input';
import { CoaPercentChangeInput } from './components/coa-percent-change-input';

import { formatPercentDisplay } from './utils/percent-field-utils';
import { COA_TOTAL_LABEL } from './tuition-step.constants';

const commonHeaderStyles =
  'h-11 border-r-2 border-white font-semibold cursor-pointer text-xs py-0 tracking-[1.6px] uppercase';

const REQUIRED_FEES_CATEGORY = 'REQUIRED_FEES';

type CellInfo = {
  renderValue?: () => unknown;
  getValue?: () => unknown;
  row: { index: number; original: Record<string, unknown> };
};

const formatCurrency = (val: unknown): string => {
  if (val == null) return '';

  return `$ ${Math.round(Number(val)).toLocaleString('en-US')}`;
};

const calcPercentChange = (prev: unknown, curr: unknown): string => {
  const prevNum = Number(prev);
  const currNum = Number(curr);

  if (curr == null) return '';
  if (!prevNum || prevNum === 0) return currNum ? '100' : '';

  const pct = ((currNum - prevNum) / prevNum) * 100;

  if (pct % 1 === 0) return String(Math.round(pct));

  const formatted = pct.toFixed(1);

  return formatted.endsWith('.0') ? String(Math.round(pct)) : formatted;
};

type GetTuitionTableSettingsArgs = {
  onAmountChange: (
    coaId: string | number,
    expenseIndex: number,
    value: unknown
  ) => void;
  onPercentChange: (
    coaId: string | number,
    expenseIndex: number,
    percent: number
  ) => void;
  onIncludeFeesChange: (coaId: string | number, value: boolean) => void;
  coaId: string | number;
  categoryLabels: Record<string, string | undefined> | undefined;
  inputsRemountKey: string | number;
};

export const getTuitionTableSettings = ({
  onAmountChange,
  onPercentChange,
  onIncludeFeesChange,
  coaId,
  categoryLabels,
  inputsRemountKey,
}: GetTuitionTableSettingsArgs) => [
  {
    id: 'category',
    accessorKey: 'category',
    header: () => <span>EXPENSES</span>,
    cell: (info: CellInfo) => {
      const category = info.renderValue?.() as string;
      const row = info.row.original as Record<string, unknown>;

      if (row.isTotalRow) {
        return (
          <span className="font-bold text-gray-700">{COA_TOTAL_LABEL}</span>
        );
      }

      const label = categoryLabels?.[category] ?? category;

      if (category === REQUIRED_FEES_CATEGORY) {
        return (
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-700 shrink-0">
              {label}
            </span>
            <div className="flex items-center gap-2">
              <Toggle
                isToggleOn={Boolean(row.includeRequiredFees)}
                onToggleChange={(checked) =>
                  onIncludeFeesChange(coaId, checked)
                }
                size="sm"
                data-testid={`include-fees-toggle-${coaId}`}
              />
              <span className="text-sm leading-5 text-gray-700 font-normal">
                Include required fees in discount rate
              </span>
            </div>
          </div>
        );
      }

      return <TruncatedCell value={label} />;
    },
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className:
      'w-[57%] text-left font-semibold text-gray-700 pl-4 py-2 max-w-0',
    enableSorting: false,
  },
  {
    id: 'previousYearAmount',
    accessorKey: 'previousYearAmount',
    header: () => <span>PRIOR YEAR</span>,
    cell: (info: CellInfo) => (
      <span
        className={classNames('text-gray-700 pl-2.5', {
          'font-semibold': info.row.original.isTotalRow,
        })}
      >
        {formatCurrency(info.getValue?.())}
      </span>
    ),
    headerClassName: `${commonHeaderStyles} pl-2.5`,
    className: 'w-[17%] py-2 text-left',
    enableSorting: false,
  },
  {
    id: 'currentYearAmount',
    accessorKey: 'currentYearAmount',
    header: () => <span>CURRENT YEAR</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original as Record<string, unknown>;

      if (row.isTotalRow) {
        return (
          <span className="text-gray-700 font-semibold pl-[22px]">
            {formatCurrency(info.getValue?.())}
          </span>
        );
      }

      const expenseIndex = info.row.index;

      return (
        <div className="px-3 py-1">
          <CoaCurrentYearAmountInput
            key={`amount-${expenseIndex}-${inputsRemountKey}`}
            coaId={coaId}
            expenseIndex={expenseIndex}
            amount={row.currentYearAmount as string | number | null}
            onAmountChange={onAmountChange}
          />
        </div>
      );
    },
    headerClassName: `${commonHeaderStyles} pl-3`,
    className: 'w-[14%]',
    enableSorting: false,
  },
  {
    id: 'percentChange',
    header: () => <span>% OF CHANGE</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original as Record<string, unknown>;
      const pctValue = calcPercentChange(
        row.previousYearAmount,
        row.currentYearAmount
      );

      if (row.isTotalRow) {
        return (
          <span className="text-gray-700 font-semibold pl-5">
            {formatPercentDisplay(pctValue || '0')}
          </span>
        );
      }

      const expenseIndex = info.row.index;

      return (
        <div className="px-3 py-1">
          <CoaPercentChangeInput
            key={`pct-${expenseIndex}-${inputsRemountKey}`}
            pctValue={pctValue}
            disabled={!Number(row.previousYearAmount)}
            coaId={coaId}
            expenseIndex={expenseIndex}
            onPercentChange={onPercentChange}
            onAmountChange={onAmountChange}
          />
        </div>
      );
    },
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md pl-3`,
    className: 'w-[12%]',
    enableSorting: false,
  },
];
